import { css } from '@emotion/react'
import Link from 'next/link'
import { useRef } from 'react'
import { useUser } from '../../../../../contexts/AuthContext'
import { convertToJst } from '@/lib/date'
import { COMPANY_HREF } from '@/lib/url'
import { isLatestEnterpriseOrTrial } from '@/lib/user'
import { IGNORED_BY_WOVN_CLASS_NAME } from '@/lib/wovn'
import { A } from '../../../../atoms/A'
import {
  ClickableIcomoonIcon,
  IcomoonIcon,
} from '../../../../atoms/IcomoonIcon'
import { TagButton, TagButtonGroup } from '../../../../atoms/TagButton'
import { Company } from '../../actions'
import {
  SelectedContactPersonRow,
  SelectedFirstInterviewDateRow,
  SelectedStatusRow,
} from '../../containers/search-result'
import { buildShareUrl } from '../../saga/history'
import { formatDatetime, formatNumber, formatRange } from '../../utils'
import CommaSeparatedList from '../comma-separated-list'
import {
  COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS,
  ColumnName,
  ColumnState,
} from '../../reducers/column'
import { ConditionState, StatusConditionState } from '../../reducers/condition'
import { Memo } from './Memo'
import { Tooltip, colors } from 'plume-ui'
import { formatLastFundingType } from '@/lib/roundtype'
import { DescriptionText } from '@/components/pages/companies/components/molucule/DescriptionText'

type Props = {
  name: ColumnName
  data: Company
  columns: ColumnState
  condition: ConditionState
  statusCondition: StatusConditionState
  selectedCompanyStatus: SelectedStatusRow | null
  setSelectedCompanyStatus: React.Dispatch<
    React.SetStateAction<SelectedStatusRow | null>
  >
  selectedCompanyContactPerson: SelectedContactPersonRow | null
  setSelectedCompanyContactPerson: React.Dispatch<
    React.SetStateAction<SelectedContactPersonRow | null>
  >
  selectedCompanyFirstInterviewDate: SelectedFirstInterviewDateRow | null
  setSelectedCompanyFirstInterviewDate: React.Dispatch<
    React.SetStateAction<SelectedFirstInterviewDateRow | null>
  >
  onTriggerEditStatus: (v: string) => void
  isInhouse: boolean
}

export const TableCell: React.FC<Props> = ({
  name,
  data,
  columns,
  condition,
  statusCondition,
  selectedCompanyStatus,
  setSelectedCompanyStatus,
  selectedCompanyContactPerson,
  setSelectedCompanyContactPerson,
  selectedCompanyFirstInterviewDate,
  setSelectedCompanyFirstInterviewDate,
  onTriggerEditStatus,
  isInhouse,
}) => {
  const statusTargetRef = useRef<HTMLTableCellElement>(null)
  const contactPersonTargetRef = useRef<HTMLTableCellElement>(null)
  const firstInterviewDateRef = useRef<HTMLTableCellElement>(null)
  const totalFundingText = (n: number) => formatNumber(n) || '-'
  const user = useUser()

  switch (name) {
    case 'name':
      return (
        <td
          data-test-id={name}
          css={css`
            // widthを使うと他のCSSで上書きされるため、min-widthとmax-widthを使う
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width!}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width!}px;
          `}
        >
          <span
            className="name"
            css={css`
              padding-right: 8px;
              white-space: break-spaces;
              display: flex;
              justify-content: space-between;
            `}
          >
            <Link
              as={`/companies/${data.companyId}`}
              href={COMPANY_HREF}
              passHref
            >
              <A
                decoration="none"
                onClick={() =>
                  sessionStorage.setItem(
                    'search_condition',
                    JSON.stringify({
                      url: buildShareUrl(condition, columns, statusCondition),
                      y_offset: window.pageYOffset,
                    })
                  )
                }
              >
                {data.name}
              </A>
            </Link>
            {isInhouse && (
              <Tooltip label="ステータスパネルを開く">
                <span
                  className="panel-icon"
                  css={css`
                    width: 20px;
                    height: 20px;
                    display: flex;
                    visibility: hidden;
                    :hover {
                      background-color: rgba(0, 0, 0, 0.08);
                      border-radius: 50%;
                    }
                    justify-content: center;
                    align-items: center;
                  `}
                >
                  <ClickableIcomoonIcon
                    name="side-panel"
                    onClick={() => onTriggerEditStatus(data.companyId)}
                    css={css`
                      font-size: 14px;
                      color: ${colors.textLight};
                      cursor: pointer;
                    `}
                    data-test-id="panel-icon"
                  />
                </span>
              </Tooltip>
            )}
          </span>
        </td>
      )
    case 'description':
      return <DescriptionText text={data[name]} dataTestId={name} />
    case 'employees':
      const value = data[name]
      return (
        <td
          data-test-id={name}
          className={`text-right ${IGNORED_BY_WOVN_CLASS_NAME}`}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
          `}
        >
          {typeof value === 'object' ? formatRange(value) : formatNumber(value)}
        </td>
      )
    case 'ipoJikasougaku':
      return (
        <td
          data-test-id={name}
          className={`text-right ${IGNORED_BY_WOVN_CLASS_NAME}`}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
          `}
        >
          {formatNumber(data[name])}
        </td>
      )
    case 'valuation':
      return (
        <td
          data-test-id={name}
          className={`text-right ${IGNORED_BY_WOVN_CLASS_NAME}`}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
          `}
        >
          <div
            className="sub-text"
            css={css`
              color: #999;
              font-size: 11px;
            `}
          >
            {data['type'] === 'IPO企業' && data[name] ? 'IPO前最終調達時' : ''}
          </div>
          {formatNumber(data[name])}
        </td>
      )
    case 'totalFunding':
      return (
        <td
          data-test-id={name}
          className={`text-right ${IGNORED_BY_WOVN_CLASS_NAME}`}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
          `}
        >
          {totalFundingText(data[name])}
        </td>
      )
    case 'updated':
    case 'created':
    case 'lastValuationDate':
      return (
        <td
          data-test-id={name}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
          `}
          className={IGNORED_BY_WOVN_CLASS_NAME}
        >
          {formatDatetime(data[name])}
        </td>
      )
    case 'tags':
      return (
        <td
          data-test-id={name}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
          `}
        >
          {data.tags.length > 0 ? (
            <TagButtonGroup className="tag-list">
              <span
                css={css`
                  display: flex;
                  flex-wrap: nowrap;
                  white-space: break-spaces;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  display: -webkit-box;
                  -webkit-box-orient: vertical;
                  -webkit-line-clamp: 2;
                  line-height: 150%;
                `}
              >
                {data.tags.slice(0, 6).map((tag, i) => (
                  <TagButton key={i} tag={tag} />
                ))}
              </span>
            </TagButtonGroup>
          ) : null}
        </td>
      )
    case 'shareholderStatus':
    case 'origin':
    case 'institute':
      return (
        <td
          data-test-id={name}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
          `}
        >
          <CommaSeparatedList
            value={data[name]}
            css={css`
              white-space: break-spaces;
            `}
          />
        </td>
      )
    case 'type':
      return (
        <td
          data-test-id={name}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
          `}
        >
          {data[name] === 'IPO企業' ? 'IPO企業' : '未公開企業'}
        </td>
      )
    case 'website':
      return (
        <td
          data-test-id={name}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
          `}
        >
          <Link
            passHref={true}
            href={data[name] != undefined ? data[name]!.toString() : ''}
          >
            <A
              css={css`
                white-space: normal;
                word-break: break-all;
              `}
              decoration="none"
              target="_blank"
              rel="noopener noreferrer"
            >
              {data[name]}
            </A>
          </Link>
        </td>
      )
    case 'latestInitialSeries':
      return (
        <td
          data-test-id={name}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
          `}
        >
          <span
            css={css`
              width: 150px;
            `}
          >
            {data['type'] === '未公開企業' ? data[name] : ''}
          </span>
        </td>
      )
    case 'cbLatestRoundType':
      const cbLatestRoundType = data[name]
      return (
        <td
          data-test-id={name}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
          `}
        >
          <div
            css={css`
              line-height: 1.5;
              word-break: break-word;
              white-space: normal;
            `}
          >
            {cbLatestRoundType ? formatLastFundingType(cbLatestRoundType) : ''}
          </div>
        </td>
      )
    case 'investingStatus':
      const status = data[name]
      if (!isLatestEnterpriseOrTrial(user)) {
        return null
      }
      return (
        <td
          data-test-id={name}
          className={isInhouse ? '' : 'disabled'}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            &:hover {
              cursor: pointer;
              &::before {
                border-right: 1px solid rgb(217, 217, 217);
                border-left: 1px solid rgb(217, 217, 217);
              }
            }
            &::before {
              content: '';
              display: block;
              width: 100%;
              height: 100%;
              background-color: transparent;
              position: absolute;
              left: 0;
              top: 0;
              pointer-events: none;
            }

            &.disabled {
              cursor: not-allowed;
              opacity: 0.3;
            }

            position: relative;
          `}
          ref={statusTargetRef}
          onClick={() => {
            if (!isInhouse) {
              return
            }
            selectedCompanyStatus != null
              ? setSelectedCompanyStatus(null)
              : setSelectedCompanyStatus({
                  companyId: data.companyId,
                  selectedStatusName: status,
                  target: statusTargetRef.current!,
                })
          }}
        >
          <div
            css={css`
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <div
              css={css`
                overflow: hidden;
                white-space: nowrap;
                text-overflow: ellipsis;
                word-break: break-all;
                white-space: break-spaces;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
                line-height: 150%;
              `}
            >
              {data[name]}
            </div>
            <IcomoonIcon
              css={css`
                font-size: 10px;
                display: block;
              `}
              name={'arrow1-down'}
            />
          </div>
        </td>
      )
    case 'contactPerson':
      if (!isLatestEnterpriseOrTrial(user)) {
        return null
      }
      const contactPerson: { firstName: string; lastName: string } | undefined =
        data[name]
      return (
        <td
          data-test-id={name}
          className={isInhouse ? '' : 'disabled'}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            &:hover {
              cursor: pointer;
              &::before {
                border-right: 1px solid rgb(217, 217, 217);
                border-left: 1px solid rgb(217, 217, 217);
              }
            }
            &::before {
              content: '';
              display: block;
              width: 100%;
              height: 100%;
              background-color: transparent;
              position: absolute;
              left: 0;
              top: 0;
              pointer-events: none;
            }
            &.disabled {
              cursor: not-allowed;
              opacity: 0.3;
            }
            position: relative;
          `}
          ref={contactPersonTargetRef}
          onClick={() => {
            if (!isInhouse) {
              return
            } else {
              selectedCompanyContactPerson != null
                ? setSelectedCompanyContactPerson(null)
                : setSelectedCompanyContactPerson({
                    companyId: data.companyId,
                    selectedContactPersonFirstName: contactPerson?.firstName,
                    selectedContactPersonLastName: contactPerson?.lastName,
                    target: contactPersonTargetRef.current!,
                  })
            }
          }}
        >
          <div
            css={css`
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <div
              css={css`
                white-space: break-spaces;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
              `}
            >
              {contactPerson &&
                `${contactPerson.lastName} ${contactPerson.firstName}`}
            </div>
            <IcomoonIcon
              css={css`
                font-size: 10px;
                display: block;
              `}
              name={'arrow1-down'}
            />
          </div>
        </td>
      )
    case 'investingStatusUpdated':
      if (!isLatestEnterpriseOrTrial(user)) {
        return null
      }
      return (
        <td
          data-test-id={name}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            &.disabled {
              cursor: not-allowed;
              opacity: 0.3;
            }
          `}
          className={`${IGNORED_BY_WOVN_CLASS_NAME} ${
            isInhouse ? '' : 'disabled'
          }`}
        >
          {convertToJst(data[name])}
        </td>
      )
    case 'memo':
      if (!isLatestEnterpriseOrTrial(user)) {
        return null
      } else {
        return (
          <td
            data-test-id={name}
            css={css`
              min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS.memo?.width}px;
              max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS.memo?.width}px;
            `}
          >
            <Memo
              text={data[name]?.content}
              shortName={data.companyId}
              onClickEdit={onTriggerEditStatus}
              disabled={!isInhouse}
            />
          </td>
        )
      }
    case 'firstInterviewDate':
      if (!isLatestEnterpriseOrTrial(user)) {
        return null
      }
      const firstInterviewDate: string | undefined = data[name]
      return (
        <td
          data-test-id={name}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            &:hover {
              cursor: pointer;
              &::before {
                border-right: 1px solid rgb(217, 217, 217);
                border-left: 1px solid rgb(217, 217, 217);
              }
            }
            &::before {
              content: '';
              display: block;
              width: 100%;
              height: 100%;
              background-color: transparent;
              position: absolute;
              left: 0;
              top: 0;
              pointer-events: none;
            }
            &.disabled {
              cursor: not-allowed;
              opacity: 0.3;
            }
            position: relative;
          `}
          className={`${IGNORED_BY_WOVN_CLASS_NAME} ${
            isInhouse ? '' : 'disabled'
          }`}
          ref={firstInterviewDateRef}
          onClick={() => {
            if (!isInhouse) return
            selectedCompanyFirstInterviewDate != null
              ? setSelectedCompanyFirstInterviewDate(null)
              : setSelectedCompanyFirstInterviewDate({
                  shortName: data.companyId,
                  selectedFirstInterviewDate: firstInterviewDate
                    ? new Date(firstInterviewDate)
                    : new Date(),
                  target: firstInterviewDateRef.current!,
                })
          }}
        >
          <div
            css={css`
              display: flex;
              flex-direction: row;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <div
              css={css`
                white-space: break-spaces;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
              `}
            >
              {convertToJst(data[name])}
            </div>
            <IcomoonIcon
              css={css`
                font-size: 10px;
                display: block;
              `}
              name={'arrow1-down'}
            />
          </div>
        </td>
      )
    case 'sourcingRoute': // NOTE: これがないと、default の中で型エラーが出る
      return <></>
    case 'myLabels': // NOTE: これがないと、default の中で型エラーが出る
      return <></>
    default:
      return (
        <td
          data-test-id={name}
          css={css`
            min-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            max-width: ${COLUMN_NAMES_WITH_STATUS_EARLY_ACCESS[name]?.width}px;
            white-space: break-spaces;
          `}
        >
          {data[name]}
        </td>
      )
  }
}
